import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save } from "lucide-react";
import api from "@/lib/api";
import { SF_Picker } from "./AdminLayout";
import { toast } from "sonner";

export default function AdminContent() {
  const [sf, setSf] = useState("jewelry");
  const [tab, setTab] = useState("storefront");
  const [cms, setCms] = useState(null);

  const load = () => api.get(`/admin/cms/${sf}`).then((r) => setCms(r.data)).catch(() => {});
  useEffect(() => { load(); }, [sf]);

  const tabs = [["storefront", "Branding"], ["homepage", "Homepage"], ["navigation", "Navigation"], ["content", "Text Blocks"], ["collections", "Collections"]];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl">Content & Pages</h1>
        <SF_Picker value={sf} onChange={setSf} />
      </div>
      <p className="text-sm text-neutral-500 mb-6">Editing <strong className="capitalize">{sf}</strong> only. Changes are scoped to this storefront and never affect the others.</p>
      <div className="flex gap-2 mb-6 border-b border-neutral-200 flex-wrap">
        {tabs.map(([k, l]) => <button key={k} data-testid={`content-tab-${k}`} onClick={() => setTab(k)} className={`px-4 py-2.5 text-sm ${tab === k ? "border-b-2 border-neutral-900 font-medium" : "text-neutral-500"}`}>{l}</button>)}
      </div>
      {!cms ? <div className="text-neutral-400">Loading…</div> : (
        <>
          {tab === "storefront" && <Branding sf={sf} data={cms.storefront} reload={load} />}
          {tab === "homepage" && <Homepage sf={sf} data={cms.homepage} reload={load} />}
          {tab === "navigation" && <Navigation sf={sf} data={cms.navigation} reload={load} />}
          {tab === "content" && <TextBlocks sf={sf} data={cms.content} reload={load} />}
          {tab === "collections" && <Collections sf={sf} data={cms.collections} reload={load} />}
        </>
      )}
    </div>
  );
}

const inp = "w-full h-10 px-3 border border-neutral-300 rounded text-sm";
const card = "bg-white border border-neutral-200 rounded-lg p-6";
const lbl = "block text-xs uppercase tracking-wide text-neutral-500 mb-1 mt-3";

function Branding({ sf, data, reload }) {
  const [f, setF] = useState(data || {});
  const save = async () => { await api.put(`/admin/cms/${sf}/storefront`, { logo_text: f.logo_text, logo_subtext: f.logo_subtext, name: f.name, tagline: f.tagline, category_image: f.category_image }); toast.success("Saved."); reload(); };
  return (<div className={`${card} max-w-xl`}>
    <h2 className="font-serif text-xl mb-4">Branding & Logo</h2>
    <label className={lbl}>Logo Text</label><input data-testid="brand-logo-text" className={inp} value={f.logo_text || ""} onChange={(e) => setF({ ...f, logo_text: e.target.value })} />
    <label className={lbl}>Logo Subtext (e.g. MEN / WOMEN)</label><input className={inp} value={f.logo_subtext || ""} onChange={(e) => setF({ ...f, logo_subtext: e.target.value })} />
    <label className={lbl}>Storefront Name</label><input className={inp} value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} />
    <label className={lbl}>Category Splash Image (entry-page panel)</label>
    <input data-testid="brand-category-image" className={inp} value={f.category_image || ""} onChange={(e) => setF({ ...f, category_image: e.target.value })} placeholder="Image URL" />
    {f.category_image && <img src={f.category_image} alt="" className="mt-3 w-full h-44 object-cover rounded border border-neutral-200" />}
    <button data-testid="brand-save" onClick={save} className="mt-5 bg-neutral-900 text-white h-10 px-6 rounded text-sm flex items-center gap-2"><Save size={15} /> Save</button>
  </div>);
}

function Homepage({ sf, data, reload }) {
  const [sections, setSections] = useState(data?.sections || []);
  const upd = (i, k, v) => { const n = [...sections]; n[i] = { ...n[i], [k]: v }; setSections(n); };
  const updPanel = (i, pi, k, v) => { const n = [...sections]; const panels = [...n[i].panels]; panels[pi] = { ...panels[pi], [k]: v }; n[i] = { ...n[i], panels }; setSections(n); };
  const save = async () => { await api.put(`/admin/cms/${sf}/homepage`, { sections }); toast.success("Homepage saved."); reload(); };

  return (<div className="space-y-4 max-w-3xl">
    {sections.map((s, i) => (
      <div key={i} className={card} data-testid={`home-section-${i}`}>
        <div className="text-xs uppercase tracking-wide text-neutral-400 mb-3">Section {i + 1} · {s.type}</div>
        {s.image !== undefined && (<><label className={lbl}>Image URL</label><input className={inp} value={s.image || ""} onChange={(e) => upd(i, "image", e.target.value)} /></>)}
        {s.eyebrow !== undefined && (<><label className={lbl}>Eyebrow</label><input className={inp} value={s.eyebrow || ""} onChange={(e) => upd(i, "eyebrow", e.target.value)} /></>)}
        {s.headline !== undefined && (<><label className={lbl}>Headline</label><input className={inp} value={s.headline || ""} onChange={(e) => upd(i, "headline", e.target.value)} /></>)}
        {s.text !== undefined && (<><label className={lbl}>Text</label><textarea className={`${inp} h-16 py-2`} value={s.text || ""} onChange={(e) => upd(i, "text", e.target.value)} /></>)}
        {s.cta_label !== undefined && (<div className="grid grid-cols-2 gap-3"><div><label className={lbl}>Button Label</label><input className={inp} value={s.cta_label || ""} onChange={(e) => upd(i, "cta_label", e.target.value)} /></div><div><label className={lbl}>Button Link</label><input className={inp} value={s.cta_href || ""} onChange={(e) => upd(i, "cta_href", e.target.value)} /></div></div>)}
        {s.panels && s.panels.map((p, pi) => (
          <div key={pi} className="border-t border-neutral-100 mt-4 pt-3"><div className="text-xs text-neutral-400">Panel {pi + 1}</div>
            <label className={lbl}>Image URL</label><input className={inp} value={p.image || ""} onChange={(e) => updPanel(i, pi, "image", e.target.value)} />
            <label className={lbl}>Title</label><input className={inp} value={p.title || ""} onChange={(e) => updPanel(i, pi, "title", e.target.value)} />
            <div className="grid grid-cols-2 gap-3"><div><label className={lbl}>Button Label</label><input className={inp} value={p.cta_label || ""} onChange={(e) => updPanel(i, pi, "cta_label", e.target.value)} /></div><div><label className={lbl}>Button Link</label><input className={inp} value={p.cta_href || ""} onChange={(e) => updPanel(i, pi, "cta_href", e.target.value)} /></div></div>
          </div>
        ))}
      </div>
    ))}
    <button data-testid="home-save" onClick={save} className="bg-neutral-900 text-white h-11 px-8 rounded text-sm flex items-center gap-2"><Save size={15} /> Save Homepage</button>
  </div>);
}

function Navigation({ sf, data, reload }) {
  const [items, setItems] = useState(data?.items || []);
  const upd = (i, k, v) => { const n = [...items]; n[i] = { ...n[i], [k]: v }; setItems(n); };
  const add = () => setItems([...items, { label: "NEW", href: "/collection/new", columns: [], image: null }]);
  const del = (i) => setItems(items.filter((_, x) => x !== i));
  const save = async () => { await api.put(`/admin/cms/${sf}/navigation`, { items }); toast.success("Navigation saved."); reload(); };

  return (<div className="space-y-3 max-w-2xl">
    {items.map((it, i) => (
      <div key={i} className={`${card} flex items-end gap-3`} data-testid={`nav-item-${i}`}>
        <div className="flex-1"><label className={lbl}>Label</label><input className={inp} value={it.label} onChange={(e) => upd(i, "label", e.target.value)} /></div>
        <div className="flex-1"><label className={lbl}>Link</label><input className={inp} value={it.href} onChange={(e) => upd(i, "href", e.target.value)} /></div>
        <div className="flex-1"><label className={lbl}>Menu Image URL</label><input className={inp} value={it.image || ""} onChange={(e) => upd(i, "image", e.target.value)} /></div>
        <button onClick={() => del(i)} className="text-red-500 p-2 mb-1"><Trash2 size={16} /></button>
      </div>
    ))}
    <div className="flex gap-3">
      <button onClick={add} className="border border-neutral-300 h-10 px-5 rounded text-sm flex items-center gap-2"><Plus size={15} /> Add Item</button>
      <button data-testid="nav-save" onClick={save} className="bg-neutral-900 text-white h-10 px-6 rounded text-sm flex items-center gap-2"><Save size={15} /> Save Navigation</button>
    </div>
  </div>);
}

function TextBlocks({ sf, data, reload }) {
  const [blocks, setBlocks] = useState(data?.blocks || {});
  const upd = (k, v) => setBlocks({ ...blocks, [k]: v });
  const save = async () => { await api.put(`/admin/cms/${sf}/content`, { blocks }); toast.success("Text saved."); reload(); };
  return (<div className={`${card} max-w-2xl space-y-1`}>
    {Object.keys(blocks).map((k) => (
      <div key={k}><label className={lbl}>{k.replace(/_/g, " ")}</label>
        {k.includes("body") || k.includes("about") || k === "footer_about" ? <textarea className={`${inp} h-20 py-2`} value={blocks[k]} onChange={(e) => upd(k, e.target.value)} /> : <input className={inp} value={blocks[k]} onChange={(e) => upd(k, e.target.value)} />}
      </div>
    ))}
    <button data-testid="text-save" onClick={save} className="mt-5 bg-neutral-900 text-white h-10 px-6 rounded text-sm flex items-center gap-2"><Save size={15} /> Save Text</button>
  </div>);
}

function Collections({ sf, data, reload }) {
  const [cols, setCols] = useState(data || []);
  const [nw, setNw] = useState({ name: "", description: "", image: "" });
  const upd = (i, k, v) => { const n = [...cols]; n[i] = { ...n[i], [k]: v }; setCols(n); };
  const saveOne = async (c) => { await api.put(`/admin/cms/collections/${c.id}`, { name: c.name, description: c.description, image: c.image }); toast.success("Collection saved."); };
  const del = async (c) => { await api.delete(`/admin/cms/collections/${c.id}`); reload(); };
  const create = async () => { if (!nw.name) return; await api.post(`/admin/cms/${sf}/collections`, nw); setNw({ name: "", description: "", image: "" }); reload(); toast.success("Collection created."); };

  return (<div className="space-y-4 max-w-2xl">
    {cols.map((c, i) => (
      <div key={c.id} className={card} data-testid={`collection-${c.slug}`}>
        <div className="flex gap-3">
          <img src={c.image} alt="" className="w-16 h-20 object-cover rounded" />
          <div className="flex-1">
            <input className={`${inp} mb-2`} value={c.name} onChange={(e) => upd(i, "name", e.target.value)} />
            <input className={`${inp} mb-2`} value={c.image || ""} onChange={(e) => upd(i, "image", e.target.value)} placeholder="Image URL" />
            <input className={inp} value={c.description || ""} onChange={(e) => upd(i, "description", e.target.value)} placeholder="Description" />
          </div>
          <div className="flex flex-col gap-2">
            <button onClick={() => saveOne(c)} className="bg-neutral-900 text-white p-2 rounded"><Save size={15} /></button>
            <button onClick={() => del(c)} className="text-red-500 p-2"><Trash2 size={15} /></button>
          </div>
        </div>
      </div>
    ))}
    <div className={card}>
      <div className="text-sm font-medium mb-3">New Collection</div>
      <input className={`${inp} mb-2`} placeholder="Name" value={nw.name} onChange={(e) => setNw({ ...nw, name: e.target.value })} />
      <input className={`${inp} mb-2`} placeholder="Image URL" value={nw.image} onChange={(e) => setNw({ ...nw, image: e.target.value })} />
      <input className={`${inp} mb-3`} placeholder="Description" value={nw.description} onChange={(e) => setNw({ ...nw, description: e.target.value })} />
      <button data-testid="collection-create" onClick={create} className="bg-neutral-900 text-white h-10 px-6 rounded text-sm flex items-center gap-2"><Plus size={15} /> Create</button>
    </div>
  </div>);
}
